import {
  ADMIN_CERTIFICATES_SETTINGS_KPI_OPTIONS,
  ADMIN_CERTIFICATES_SETTINGS_NUMBER_FIELDS,
  AdminCertificatesSettingsFormValues,
  AdminCertificatesSettingsNumberField,
  AdminCertificatesSettingsPayload,
  AdminCertificatesSettingsRow,
  getAdminCertificatesSettingsColumnLabel,
} from "../models/admin-certificates-settings.models";

const KPI_OPTIONS = new Set<string>(ADMIN_CERTIFICATES_SETTINGS_KPI_OPTIONS);
const NUMBER_FIELDS = new Set<string>(ADMIN_CERTIFICATES_SETTINGS_NUMBER_FIELDS);

export const toAdminCertificatesSettingsGridRow = (
  row: AdminCertificatesSettingsRow,
): AdminCertificatesSettingsRow => ({
  ...row,
  gridRowId: row.ID ? `certificates-settings-${row.ID}` : `certificates-settings-${Date.now()}`,
});

export const createAdminCertificatesSettingsFormValues = (
  row: AdminCertificatesSettingsRow,
  vesselTypeOptions: string[] = [],
): AdminCertificatesSettingsFormValues => ({
  EXCLUDED_VESSEL_TYPES: parseAdminCertificatesSettingsVesselTypes(row.EXCLUDED_VESSEL_TYPES, vesselTypeOptions),
  KPI_INCLUDED: row.KPI_INCLUDED ? "True" : "False",
  EXPIRY_NOTICE_DAYS: formatNumber(row.EXPIRY_NOTICE_DAYS),
  ENDORSEMENT_NOTICE_DAYS: formatNumber(row.ENDORSEMENT_NOTICE_DAYS),
});

export const toAdminCertificatesSettingsPayload = (
  values: AdminCertificatesSettingsFormValues,
): AdminCertificatesSettingsPayload => ({
  EXCLUDED_VESSEL_TYPES: normalizeVesselTypeSelections(values.EXCLUDED_VESSEL_TYPES),
  KPI_INCLUDED: normalizeKpiIncluded(values.KPI_INCLUDED),
  EXPIRY_NOTICE_DAYS: normalizeNumber(values.EXPIRY_NOTICE_DAYS, "EXPIRY_NOTICE_DAYS"),
  ENDORSEMENT_NOTICE_DAYS: normalizeNumber(values.ENDORSEMENT_NOTICE_DAYS, "ENDORSEMENT_NOTICE_DAYS"),
});

export const formatBooleanValue = (value: unknown): string => {
  if (value === true) {
    return "True";
  }

  if (value === false) {
    return "False";
  }

  return "";
};

export function getAdminCertificatesSettingsVesselTypeOptions(options: string[], selectedValues: string[]): string[] {
  return normalizeVesselTypeSelections([...options, ...selectedValues]).sort((first, second) =>
    first.localeCompare(second, undefined, { sensitivity: "base" }),
  );
}

export function parseAdminCertificatesSettingsVesselTypes(
  value: string | null | undefined,
  options: string[] = [],
): string[] {
  const optionLookup = buildVesselTypeOptionLookup(options);
  return normalizeVesselTypeSelections(
    String(value ?? "")
      .split(",")
      .map((vesselType) => optionLookup[vesselType.trim().toLowerCase()] ?? vesselType),
  );
}

function formatNumber(value: number | null | undefined): string {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value);
}

function normalizeKpiIncluded(value: string): boolean {
  if (!KPI_OPTIONS.has(value)) {
    throw new Error("KPI Included must be True or False.");
  }

  return value === "True";
}

function normalizeNumber(value: string, field: AdminCertificatesSettingsNumberField): number | null {
  if (!NUMBER_FIELDS.has(field)) {
    throw new Error(`Unsupported numeric field ${field}.`);
  }

  const text = value.trim();
  if (!text) {
    return null;
  }

  const numberValue = Number(text);
  if (!Number.isInteger(numberValue) || numberValue < 0) {
    throw new Error(`${getAdminCertificatesSettingsColumnLabel(field)} must be a non-negative integer or blank.`);
  }

  return numberValue;
}

function buildVesselTypeOptionLookup(options: string[]): Record<string, string> {
  return options.reduce<Record<string, string>>((lookup, option) => {
    const text = option.trim();
    const normalizedText = text.toLowerCase();
    if (text && !lookup[normalizedText]) {
      lookup[normalizedText] = text;
    }

    return lookup;
  }, {});
}

function normalizeVesselTypeSelections(values: string[]): string[] {
  return values.reduce<string[]>((vesselTypes, value) => {
    const text = value.trim();
    if (!text) {
      return vesselTypes;
    }

    const normalizedText = text.toLowerCase();
    const alreadyAdded = vesselTypes.some((vesselType) => vesselType.toLowerCase() === normalizedText);
    if (!alreadyAdded) {
      vesselTypes.push(text);
    }

    return vesselTypes;
  }, []);
}
